'use client'

/**
 * Message-row Losi:
 * - rest / done → `logo`
 * - on send → pops with `idle` or `comet`, then ambient (connect / drift / comet)
 * - tool markers → specialized `agent*` hold (engine morphs via setState)
 * - face stays `neutre`
 * - every click → `jiggle` (even at rest logo) + five-tap Games ritual
 */

import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react'

import { LosiAvatar } from '@/components/losi-bot/losi-avatar'
import { useLosiGamesOptional } from '@/components/losi-games/losi-games-provider'
import {
  AMBIENT_LOADER_STATES,
  isToolHoldPose,
  type ChatLosiPose
} from '@/lib/losi-avatar/chat-status'
import { STATE_BY_ID, type StateId } from '@/lib/losi-engine/states'
import { LOSI_JIGGLE_MS, losiMarkTapOrigin } from '@/lib/losi-games/mark-tap'
import { cn } from '@/lib/utils'

const IDLE_HOLD_MS = 700
const COMET_HOLD_MS = 2600
const AMBIENT_PAD_MS = 450

const INTROS = ['idle', 'comet'] as const
type IntroId = (typeof INTROS)[number]

const DEFAULT_POSE: ChatLosiPose = {
  state: 'drift',
  status: 'streaming',
  drive: 'ambient'
}

function pickIntro(): IntroId {
  return INTROS[Math.floor(Math.random() * INTROS.length)]!
}

function introHoldMs(intro: IntroId): number {
  return intro === 'comet' ? COMET_HOLD_MS : IDLE_HOLD_MS
}

function holdMsFor(state: StateId): number {
  const def = STATE_BY_ID.get(state)
  const seconds = Math.max(def?.minDuration ?? 0, def?.duration ?? 2.2)
  return Math.round(seconds * 1000) + AMBIENT_PAD_MS
}

function nextAmbient(current: StateId, avoid?: StateId | null): StateId {
  const pool = AMBIENT_LOADER_STATES.filter((id) => id !== current && id !== avoid)
  const pickFrom = pool.length > 0 ? pool : [...AMBIENT_LOADER_STATES]
  return pickFrom[Math.floor(Math.random() * pickFrom.length)]!
}

export interface MessageLosiMarkProps {
  size?: number
  className?: string
  /** True while this assistant turn is generating / tooling. */
  active: boolean
  pose?: ChatLosiPose
  shape?: string
  hoverState?: StateId | null
}

export function MessageLosiMark({
  size = 32,
  className,
  active,
  pose = DEFAULT_POSE,
  shape,
  hoverState = null
}: MessageLosiMarkProps) {
  const introRef = useRef<IntroId>('idle')
  const [introDone, setIntroDone] = useState(false)
  const [ambient, setAmbient] = useState<StateId>('drift')
  const [jiggling, setJiggling] = useState(false)
  const jiggleTimer = useRef(0)
  const wasActive = useRef(false)

  const toolHold = active && isToolHoldPose(pose)

  useEffect(() => {
    if (active && !wasActive.current) {
      introRef.current = pickIntro()
      setIntroDone(false)
      setAmbient(nextAmbient(introRef.current))
      const timer = window.setTimeout(() => setIntroDone(true), introHoldMs(introRef.current))
      wasActive.current = true
      return () => window.clearTimeout(timer)
    }

    if (!active && wasActive.current) {
      setIntroDone(true)
      wasActive.current = false
    }
  }, [active])

  useEffect(() => {
    if (toolHold) setIntroDone(true)
  }, [toolHold])

  useEffect(() => {
    if (!active || !introDone || jiggling || toolHold) return

    const timer = window.setTimeout(() => {
      setAmbient((current) => nextAmbient(current, introRef.current))
    }, holdMsFor(ambient))

    return () => window.clearTimeout(timer)
  }, [active, introDone, jiggling, toolHold, ambient])

  const wasToolHold = useRef(toolHold)
  useEffect(() => {
    if (wasToolHold.current && !toolHold && active && introDone) {
      setAmbient((current) => nextAmbient(current))
    }
    wasToolHold.current = toolHold
  }, [toolHold, active, introDone])

  useEffect(() => () => window.clearTimeout(jiggleTimer.current), [])

  const state: StateId = useMemo(() => {
    if (jiggling) return 'jiggle'
    if (!active) return 'logo'
    if (!introDone) return introRef.current
    if (toolHold) return pose.state
    return ambient
  }, [active, jiggling, introDone, toolHold, pose.state, ambient])

  const games = useLosiGamesOptional()

  const onClick = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      games?.registerLosiTap(losiMarkTapOrigin(e))
      window.clearTimeout(jiggleTimer.current)
      setJiggling(true)
      jiggleTimer.current = window.setTimeout(() => setJiggling(false), LOSI_JIGGLE_MS)
    },
    [games]
  )

  return (
    <LosiAvatar
      state={state}
      expression="neutre"
      size={size}
      shape={shape}
      fitBody
      hoverState={jiggling ? null : hoverState}
      className={cn(className)}
      onClick={onClick}
      title="Click to jiggle"
    />
  )
}
